/**
 * One week's NFL games, keyed by franchise: kickoff, status, opponent, home/away, and who is
 * on bye.
 *
 * The (franchise, week) -> game map had been written four times across the NFL surfaces
 * (My Team's lock/opponent read among them), each with its own filters and alias lookup. This
 * is the one read new surfaces use instead of a fifth copy.
 *
 * ── THE THREE FILTERS ARE NOT OPTIONAL ──────────────────────────────────────
 *
 * season_year + season_type 'REG' + week. SAB-811/823: without season_year a 2026 league read
 * 2025's schedule; SAB-746: without season_type, PRE week N and REG week N collide.
 *
 * ── ALL 32 ALIASES ──────────────────────────────────────────────────────────
 *
 * The opponent is almost never a franchise the caller already has in hand, so the alias map
 * is always the whole league. Filtering it to "franchises I know about" is exactly how My
 * Team rendered "Sun 1:00 PM" with no opponent from SAB-1003 on.
 *
 * ── FAILURE IS NOT "EVERYONE IS ON BYE" ─────────────────────────────────────
 *
 * A failed read throws, so the caller sees no data, not an empty schedule. An empty schedule
 * would make every franchise look like it is on bye.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { GameContext } from '@/lib/nflGameLine';

export interface NflWeekGame extends GameContext {
  scheduled: string;
  opponentId: string | null;
}

export interface NflWeekGames {
  /** Franchise id -> this week's game. Absent means no game: a bye. */
  byFranchise: Record<string, NflWeekGame>;
  /** Every franchise's alias, not just the ones playing. */
  aliasById: Record<string, string>;
  allFranchiseIds: string[];
}

interface FranchiseRow { id: string; alias: string | null }
interface GameRow {
  home_franchise_id: string | null;
  away_franchise_id: string | null;
  scheduled: string | null;
  status: string | null;
}

/** Pure, so the map can be tested without a client. */
export function buildWeekGames(franchises: FranchiseRow[], games: GameRow[]): NflWeekGames {
  const aliasById: Record<string, string> = {};
  for (const f of franchises) aliasById[f.id] = f.alias || '';

  const byFranchise: Record<string, NflWeekGame> = {};
  for (const g of games) {
    if (!g.scheduled) continue;
    const sides: [string | null, string | null, boolean][] = [
      [g.home_franchise_id, g.away_franchise_id, true],
      [g.away_franchise_id, g.home_franchise_id, false],
    ];
    for (const [self, other, isHome] of sides) {
      if (!self) continue;
      byFranchise[self] = {
        scheduled: g.scheduled,
        status: g.status,
        opponentId: other,
        // Unknown opponent stays null; gameLine then shows the time alone rather than a guess.
        opponentAlias: other ? (aliasById[other] || null) : null,
        isHome,
      };
    }
  }
  return { byFranchise, aliasById, allFranchiseIds: franchises.map((f) => f.id) };
}

export function useNflWeekGames(
  week: number | null | undefined,
  seasonYear: number | null | undefined,
  enabled = true,
) {
  const query = useQuery({
    queryKey: ['nfl-week-games', seasonYear, week],
    enabled: enabled && !!week && !!seasonYear,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<NflWeekGames> => {
      const [franchises, games] = await Promise.all([
        supabase.from('nfl_franchises').select('id, alias'),
        // `as any`: a third chained .eq() on nfl_games tips the generated types into TS2589
        // (the same cast every nfl_games read in the app carries). Rows are typed below.
        (supabase as any)
          .from('nfl_games')
          .select('home_franchise_id, away_franchise_id, scheduled, status')
          .eq('season_year', seasonYear)
          .eq('season_type', 'REG')
          .eq('week', week),
      ]);
      if (franchises.error) throw franchises.error;
      if (games.error) throw games.error;
      return buildWeekGames(
        (franchises.data ?? []) as FranchiseRow[],
        (games.data ?? []) as GameRow[],
      );
    },
  });

  const data = query.data;
  return {
    ...query,
    /** This franchise's game, or null on a bye or before the data has loaded. */
    gameFor: (franchiseId: string | null | undefined): NflWeekGame | null =>
      (data && franchiseId ? data.byFranchise[franchiseId] ?? null : null),
    /** True only when the schedule has LOADED and this franchise has no game. */
    isBye: (franchiseId: string | null | undefined): boolean =>
      !!data && !!franchiseId && franchiseId in data.aliasById && !data.byFranchise[franchiseId],
  };
}
